// "Where am I?" panel — live device position, accuracy, heading and address.

import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavStore } from '../../stores/navStore';
import { useMapStore } from '../../stores';
import {
  getCurrentPosition,
  watchPosition,
  reverseGeocode,
  formatAccuracy,
  formatSpeed,
  geolocationUnavailableReason,
  fixQualityHint,
  GeoError,
  type WhereAmI,
} from '../../lib/geolocation';
import { headingToCompass } from '../../lib/routing';
import {
  updateTrack,
  recordSpeedSample,
  formatMetres,
  formatStopwatch,
} from '../../lib/traffic';
import { cn } from '../../lib/utils';
import { AccuracyIcon, OriginMarkerIcon } from './NavIcons';
import {
  X,
  Crosshair,
  Loader2,
  Navigation,
  Compass,
  Gauge,
  MapPin,
  AlertTriangle,
  Info,
  Radio,
  Route as RouteIcon,
  Locate,
} from 'lucide-react';

export function WhereAmIPanel() {
  const {
    whereAmI,
    setWhereAmI,
    locating,
    setLocating,
    locationError,
    setLocationError,
    followDevice,
    setFollowDevice,
    setAddress,
    openDirections,
    closePanel,
    setOrigin,
    track,
    setTrack,
    pushTrail,
    resetTrack,
  } = useNavStore();

  const { setMapState } = useMapStore();

  const unwatchRef = useRef<(() => void) | null>(null);
  const geocodeAbortRef = useRef<AbortController | null>(null);
  const lastGeocodedRef = useRef<string>('');
  const receivedFixRef = useRef(0);
  const didInitialZoomRef = useRef(false);
  const [stalled, setStalled] = useState(false);

  // Non-null when the current fix is not GPS-grade (Wi-Fi / cell / IP based).
  const accuracyHint = whereAmI ? fixQualityHint(whereAmI.accuracy) : null;

  // Reset the stall flag whenever tracking is switched on
  useEffect(() => {
    if (followDevice) {
      setStalled(false);
      didInitialZoomRef.current = false;
    }
  }, [followDevice]);

  // Live ticking for the stopwatch while tracking
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!followDevice) return;
    const iv = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(iv);
  }, [followDevice]);

  // --- one-shot locate ---
  const locate = useCallback(async () => {
    setLocating(true);
    setLocationError(null);
    try {
      const pos = await getCurrentPosition();
      setWhereAmI(pos);
      setMapState({ center: pos.coords, zoom: 17 });
      // Kick off reverse geocoding for this fix
      lastGeocodedRef.current = '';
      setAddress(null, true);
    } catch (err) {
      setLocationError(
        err instanceof GeoError ? err.message : 'Could not get your location.'
      );
    } finally {
      setLocating(false);
    }
  }, [setLocating, setLocationError, setWhereAmI, setMapState, setAddress]);

  // --- initial load: locate once and start tracking straight away ---
  useEffect(() => {
    // Start with live tracking ON so the feature works without a click.
    // If geolocation is unavailable we fall back to a one-shot fix.
    const blocked = geolocationUnavailableReason();
    if (blocked) {
      setLocationError(blocked.message);
      setFollowDevice(false);
      if (!whereAmI) locate();
    } else {
      // The watch delivers the first fix itself, so do not also fire a
      // competing getCurrentPosition request.
      setFollowDevice(true);
    }

    return () => {
      unwatchRef.current?.();
      geocodeAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- follow mode: continuous tracking ---
  useEffect(() => {
    if (!followDevice) {
      unwatchRef.current?.();
      unwatchRef.current = null;
      return;
    }

    unwatchRef.current = watchPosition(
      (value) => {
        receivedFixRef.current = Date.now();

        // Keep the previously resolved address while the new fix resolves
        setWhereAmI((prev: WhereAmI | null) =>
          prev ? { ...value, address: prev.address, resolvingAddress: true } : value
        );
        // Zoom in on the very first fix so the device dot is actually visible;
        // afterwards just keep the map centred without yanking the zoom.
        if (!didInitialZoomRef.current) {
          didInitialZoomRef.current = true;
          setMapState({ center: value.coords, zoom: 17 });
        } else {
          setMapState({ center: value.coords });
        }
        setLocationError(null);

        // Feed the live-tracking session
        setTrack((prev) => updateTrack(prev, value));
        pushTrail([value.coords.lng, value.coords.lat]);

        // Every moving fix is a free traffic observation
        recordSpeedSample(value.speed);
      },
      (err) => {
        setLocationError(err.message);
        setFollowDevice(false);
      }
    );

    // Desktop browsers often return one fix and then go quiet (Wi-Fi
    // positioning does not stream). Detect that so the toggle does not just
    // look dead.
    receivedFixRef.current = Date.now();
    const stallIv = setInterval(() => {
      const quietFor = Date.now() - receivedFixRef.current;
      if (quietFor > 15_000) {
        setStalled(true);
      }
    }, 3000);

    return () => {
      clearInterval(stallIv);
      unwatchRef.current?.();
      unwatchRef.current = null;
    };
  }, [followDevice, setWhereAmI, setMapState, setLocationError, setFollowDevice]);

  // --- reverse geocode whenever coordinates move enough ---
  useEffect(() => {
    if (!whereAmI) return;

    const key = `${whereAmI.coords.lat.toFixed(5)},${whereAmI.coords.lng.toFixed(5)}`;
    if (key === lastGeocodedRef.current) return;
    lastGeocodedRef.current = key;

    geocodeAbortRef.current?.abort();
    const ctrl = new AbortController();
    geocodeAbortRef.current = ctrl;

    setAddress(null, true);
    reverseGeocode(whereAmI.coords, { signal: ctrl.signal })
      .then((addr) => setAddress(addr, false))
      .catch(() => setAddress(null, false));
  }, [whereAmI?.coords.lat, whereAmI?.coords.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRouteFromHere = () => {
    if (!whereAmI) return;
    setOrigin({ coords: whereAmI.coords, label: whereAmI.address || 'My location' });
    openDirections();
  };

  return (
    <div className="flex h-full flex-col bg-white">
      {/* ---------- Header ---------- */}
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Locate className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-semibold text-slate-900">Where am I?</h2>
        </div>
        <button
          onClick={closePanel}
          className="rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          aria-label="Close location panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ---------- Body ---------- */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Error */}
        {locationError && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-amber-800">{locationError}</p>
              {locationError.includes('http://') && (
                <p className="mt-1.5 text-[11px] text-amber-700">
                  Browsers only expose location on <code>https://</code> or{' '}
                  <code>localhost</code>. Use{' '}
                  <code>http://localhost:{typeof window !== 'undefined' ? window.location.port : '3000'}</code>{' '}
                  or set up HTTPS.
                </p>
              )}
              <button
                onClick={locate}
                className="mt-2 text-xs font-medium text-amber-900 underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* Tracking went quiet — usually desktop Wi-Fi positioning */}
        {followDevice && stalled && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <p className="text-[11px] text-blue-800">
              No new position in the last 15s. Desktop browsers often return a
              single fix over Wi-Fi. Live tracking works properly on a phone,
              or with a GPS source.
            </p>
          </div>
        )}

        {/* Low-accuracy fix — explain that this is a tower/Wi-Fi position
            rather than pretending the dot is exact. */}
        {accuracyHint && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-[11px] text-amber-800">{accuracyHint}</p>
          </div>
        )}

        {/* Main position card */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-primary-50 to-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-700">
                Your location
              </p>

              {whereAmI ? (
                <>
                  <p className="mt-1 break-words text-sm font-medium leading-snug text-slate-900">
                    {whereAmI.address ??
                      (whereAmI.resolvingAddress ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-400">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          resolving address…
                        </span>
                      ) : (
                        'Unknown address'
                      ))}
                  </p>

                  <p className="mt-1 font-mono text-[11px] text-slate-500">
                    {whereAmI.coords.lat.toFixed(6)}, {whereAmI.coords.lng.toFixed(6)}
                  </p>
                </>
              ) : (
                <p className="mt-1 text-sm text-slate-500">
                  {locating ? 'Locating…' : 'No fix yet'}
                </p>
              )}
            </div>

            <button
              onClick={locate}
              disabled={locating}
              title="Refresh my location"
              className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition-colors hover:border-slate-300 hover:text-primary-600 disabled:opacity-50"
            >
              {locating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Crosshair className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Live tracking stats */}
        {followDevice && track.points > 0 && (
          <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-primary-700">
                Live tracking
              </p>
              <button
                onClick={resetTrack}
                className="text-[10px] font-medium text-primary-700 underline hover:no-underline"
              >
                Reset
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label="Distance" value={formatMetres(track.distance)} />
              <MiniStat label="Moving" value={formatStopwatch(track.movingSeconds)} />
              <MiniStat
                label="Avg speed"
                value={
                  track.averageSpeed != null ? `${track.averageSpeed.toFixed(1)} km/h` : '—'
                }
              />
              <MiniStat
                label="Max speed"
                value={track.maxSpeed > 0 ? `${track.maxSpeed.toFixed(0)} km/h` : '—'}
              />
            </div>
            <p className="mt-2 text-[10px] text-primary-700/80">
              {track.points} GPS fixes · trail drawn on the map. Moving readings
              also feed your local traffic estimate.
            </p>
          </div>
        )}

        {/* Metrics */}
        {whereAmI && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Metric
              icon={AccuracyIcon}
              label="Accuracy"
              value={formatAccuracy(whereAmI.accuracy)}
            />
            <Metric
              icon={Compass}
              label="Heading"
              value={headingToCompass(
                whereAmI.heading != null ? (whereAmI.heading * Math.PI) / 180 : null
              )}
            />
            <Metric
              icon={Gauge}
              label="Speed"
              value={formatSpeed(whereAmI.speed)}
            />
          </div>
        )}

        {/* Live tracking toggle */}
        <button
          onClick={() => setFollowDevice(!followDevice)}
          className={cn(
            'mt-3 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors',
            followDevice
              ? 'border-primary-500 bg-primary-50'
              : 'border-slate-200 hover:bg-slate-50'
          )}
        >
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
              followDevice ? 'bg-primary-500' : 'bg-slate-100'
            )}
          >
            <Radio
              className={cn(
                'h-4 w-4',
                followDevice ? 'animate-pulse text-white' : 'text-slate-500'
              )}
            />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p
              className={cn(
                'text-sm font-medium',
                followDevice ? 'text-primary-800' : 'text-slate-700'
              )}
            >
              Live tracking
            </p>
            <p className="text-xs text-slate-500">
              {followDevice ? 'Following your position' : 'Update as you move'}
            </p>
          </div>
          {/* Switch */}
          <span
            className={cn(
              'relative h-[18px] w-8 shrink-0 rounded-full transition-colors duration-200',
              followDevice ? 'bg-primary-500' : 'bg-slate-300'
            )}
          >
            <span
              className={cn(
                'absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow transition-transform duration-200',
                followDevice ? 'translate-x-[16px]' : 'translate-x-[2px]'
              )}
            />
          </span>
        </button>

        {/* Actions */}
        <div className="mt-3 space-y-2">
          <button
            onClick={handleRouteFromHere}
            disabled={!whereAmI}
            className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100">
              <RouteIcon className="h-4 w-4 text-primary-600" />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-sm font-medium text-slate-800">Directions from here</p>
              <p className="text-xs text-slate-500">Use this as your starting point</p>
            </div>
          </button>

          {whereAmI && (
            <button
              onClick={() => setMapState({ center: whereAmI.coords, zoom: 18, pitch: 0 })}
              className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 transition-colors hover:bg-slate-50"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                <Navigation className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-medium text-slate-800">Centre on me</p>
                <p className="text-xs text-slate-500">Zoom in to your exact position</p>
              </div>
            </button>
          )}
        </div>

        {/* Privacy note */}
        <p className="mt-4 flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-400">
          <MapPin className="mt-px h-3 w-3 shrink-0" />
          Your location stays in this browser tab. It is sent only to the free OpenStreetMap
          Nominatim service to resolve your street address, and is never stored on a server.
        </p>
      </div>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 text-center">
      <Icon className="mx-auto mb-1 h-4 w-4 text-slate-400" />
      <p className="truncate text-xs font-semibold text-slate-800">{value}</p>
      <p className="mt-0.5 truncate text-[10px] uppercase tracking-wide text-slate-400">
        {label}
      </p>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-primary-100 bg-white px-2 py-1.5">
      <p className="truncate text-xs font-semibold text-slate-800">{value}</p>
      <p className="mt-0.5 truncate text-[10px] uppercase tracking-wide text-slate-400">
        {label}
      </p>
    </div>
  );
}

export default WhereAmIPanel;